import styled from "@emotion/styled";
import { Button, DatePicker, Input, Select, Space } from "antd";
import dayjs from "dayjs";
import { FC, useEffect, useState } from "react";
import {
  CREDIT_SYSTEM_OPTIONS,
  CREDIT_TYPE_OPTIONS,
  CreditSearchDraft,
} from "./creditTransactionOptions";

interface CreditTransactionSearchFormProps {
  applied: CreditSearchDraft;
  onSearch: (next: CreditSearchDraft) => void;
  onClear: () => void;
  /** ซ่อน dropdown "ระบบ" — ใช้กับแท็บ "จัดการเครดิต" ที่ล็อค type=TOPUP อยู่แล้ว (เติมเครดิตไม่มี
   * system_type ผูกอยู่ ตัวกรองนี้ไม่มีความหมายในแท็บนั้น) */
  hideSystemType?: boolean;
  /** ซ่อน dropdown "รายการ" — ใช้กับแท็บ "จัดการเครดิต" ที่ล็อค type=TOPUP ไว้แล้ว ไม่ต้องให้เลือกซ้ำ */
  hideType?: boolean;
}

const FIELD_HEIGHT_STYLE = { height: "32px" } as const;

const CreditTransactionSearchForm: FC<CreditTransactionSearchFormProps> = ({
  applied,
  onSearch,
  onClear,
  hideSystemType,
  hideType,
}) => {
  const [tempKeyword, setTempKeyword] = useState(applied.keyword);
  const [tempDateFrom, setTempDateFrom] = useState(applied.date_from);
  const [tempDateTo, setTempDateTo] = useState(applied.date_to);
  const [tempSystemType, setTempSystemType] = useState(applied.system_type);
  const [tempType, setTempType] = useState(applied.type);

  useEffect(() => {
    setTempKeyword(applied.keyword);
    setTempDateFrom(applied.date_from);
    setTempDateTo(applied.date_to);
    setTempSystemType(applied.system_type);
    setTempType(applied.type);
  }, [applied]);

  const toYmd = (date: unknown): string =>
    date ? dayjs(date as never).format("YYYY-MM-DD") : "";

  const handleSearch = () => {
    onSearch({
      date_from: tempDateFrom,
      date_to: tempDateTo,
      system_type: tempSystemType,
      type: tempType,
      keyword: tempKeyword,
    });
  };

  const handleClear = () => {
    setTempKeyword("");
    setTempDateFrom("");
    setTempDateTo("");
    setTempSystemType("");
    setTempType("");
    onClear();
  };

  const handleRangeChange = (dates: unknown) => {
    const range = dates as [unknown, unknown] | null;
    setTempDateFrom(range?.[0] ? toYmd(range[0]) : "");
    setTempDateTo(range?.[1] ? toYmd(range[1]) : "");
  };

  const showClearBtn =
    !!tempKeyword ||
    !!tempDateFrom ||
    !!tempDateTo ||
    (!hideSystemType && !!tempSystemType) ||
    (!hideType && !!tempType);

  return (
    <ButtonWrapper>
      <Space.Compact>
        {!hideSystemType && (
          <Select
            allowClear
            placeholder="ระบบ"
            value={tempSystemType || undefined}
            onChange={(v) => setTempSystemType(v ?? "")}
            options={CREDIT_SYSTEM_OPTIONS}
            style={{ minWidth: 160, width: 160, ...FIELD_HEIGHT_STYLE }}
          />
        )}
        {!hideType && (
          <Select
            allowClear
            placeholder="รายการ"
            value={tempType || undefined}
            onChange={(v) => setTempType(v ?? "")}
            options={CREDIT_TYPE_OPTIONS}
            optionFilterProp="label"
            showSearch
            style={{ minWidth: 260, width: 260, ...FIELD_HEIGHT_STYLE }}
            popupMatchSelectWidth={320}
          />
        )}
        <Input
          style={FIELD_HEIGHT_STYLE}
          placeholder="เลขที่ทำรายการ / เลขพัสดุ / หมายเหตุ"
          value={tempKeyword}
          onChange={(e) => setTempKeyword(e.target.value)}
          onPressEnter={handleSearch}
        />
        <DatePicker.RangePicker
          style={{ width: 240 }}
          placeholder={["วันที่จาก", "วันที่ถึง"]}
          format="DD/M/YYYY"
          placement="topRight"
          inputReadOnly
          value={
            tempDateFrom && tempDateTo
              ? ([dayjs(tempDateFrom), dayjs(tempDateTo)] as never)
              : null
          }
          onChange={handleRangeChange}
        />
        <Button type="default" onClick={handleSearch}>
          ค้นหา
        </Button>
        {showClearBtn ? (
          <Button type="default" onClick={handleClear}>
            X
          </Button>
        ) : null}
      </Space.Compact>
    </ButtonWrapper>
  );
};

export default CreditTransactionSearchForm;

/** รูปแบบเดียวกับ OrderTabsCard/SearchOrderForm */
const ButtonWrapper = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  height: 100%;
`;
